import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/helpers/src/internationalization/react';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {AbsoluteDateTimeFormatter} from '../../../../../../srcCommon/formatters/dateTime/absoluteDateTimeFormatter';
import {TimeFormatsEnum} from '../../../../../helpers/dateTimeHelpers';
import {ConversationTeamStatusesEnum} from '../../../../../models/api/conversationCommonTypes';
import {ConversationModel} from '../../../../../models/api/conversationModel';
import {InboxModel} from '../../../../../models/api/inboxModel';
import {ReminderModel} from '../../../../../models/api/reminderModel';
import {StaticViewerBanner} from './staticViewerBanner';

/*
 * Props.
 */

export interface StaticViewerReminder {
  reminder: ReminderModel;
  teammateName: string;
}

interface StaticViewerProps {
  conversation: ConversationModel;
  teamInboxes: ReadonlyArray<InboxModel>;
  teamReminder?: StaticViewerReminder;
  timeFormat: TimeFormatsEnum;
  teamActivityTeammateName?: string;
  teamActivityDate?: number;
}

/*
 * Style.
 */

const StyledStaticViewerSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
`;

/*
 * Component.
 */

export const StaticViewer: FC<StaticViewerProps> = (props) => {
  // If this is not a private conversation, nothing to do.
  const {conversation, teamInboxes, teamReminder, timeFormat, teamActivityTeammateName, teamActivityDate} =
    props;
  if (teamInboxes.length === 0) {
    return null;
  }

  // Snoozed.
  if (teamReminder) {
    const {reminder, teammateName} = teamReminder;

    const snoozedBody = teammateName ? (
      <Trans id="activity-static-snoozed-with-teammate">
        <strong>
          Snoozed until <AbsoluteDateTimeFormatter value={reminder.remindAt} timeFormat={timeFormat} />
        </strong>{' '}
        in {renderInboxName(teamInboxes)} by {teammateName}
      </Trans>
    ) : (
      <Trans id="activity-static-snoozed-without-teammate">
        <strong>
          Snoozed until <AbsoluteDateTimeFormatter value={reminder.remindAt} timeFormat={timeFormat} />
        </strong>{' '}
        in {renderInboxName(teamInboxes)}
      </Trans>
    );

    return (
      <StaticViewerBanner
        iconName="snoozeFilledSmall"
        color={PaletteColorsEnum.ORANGE}
        date={teamActivityDate}
      >
        <StyledStaticViewerSpan>{snoozedBody}</StyledStaticViewerSpan>
      </StaticViewerBanner>
    );
  }

  // Archived.
  if (conversation.teamStatus === ConversationTeamStatusesEnum.ARCHIVED) {
    const archivedBody = teamActivityTeammateName ? (
      <Trans id="activity-static-archived-with-teammate">
        <strong>Archived</strong> in {renderInboxName(teamInboxes)} by {teamActivityTeammateName}
      </Trans>
    ) : (
      <Trans id="activity-static-archived-without-teammate">
        <strong>Archived</strong> in {renderInboxName(teamInboxes)}
      </Trans>
    );

    return (
      <StaticViewerBanner
        iconName="archiveFilledSmall"
        color={PaletteColorsEnum.GREEN}
        date={teamActivityDate}
      >
        <StyledStaticViewerSpan>{archivedBody}</StyledStaticViewerSpan>
      </StaticViewerBanner>
    );
  }

  // Trashed.
  if (conversation.teamStatus === ConversationTeamStatusesEnum.TRASHED) {
    const trashedBody = teamActivityTeammateName ? (
      <Trans id="activity-static-trashed-with-teammate">
        <strong>Trashed</strong> in {renderInboxName(teamInboxes)} by {teamActivityTeammateName}
      </Trans>
    ) : (
      <Trans id="activity-static-trashed-without-teammate">
        <strong>Trashed</strong> in {renderInboxName(teamInboxes)}
      </Trans>
    );

    return (
      <StaticViewerBanner iconName="trashFilledSmall" color={PaletteColorsEnum.RED} date={teamActivityDate}>
        <StyledStaticViewerSpan>{trashedBody}</StyledStaticViewerSpan>
      </StaticViewerBanner>
    );
  }

  return null;
};

/*
 * Helpers.
 */

function renderInboxName(inboxes: ReadonlyArray<InboxModel>) {
  // Multiple inboxes: generic text.
  if (inboxes.length !== 1) {
    return <Trans id="activity-static-multipleinboxes">{inboxes.length} inboxes</Trans>;
  }

  // Otherwise, inbox name.
  return inboxes[0].name;
}
